import React from 'react'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  TextField
} from '@material-ui/core'
import { Controller, useController } from 'react-hook-form'
import CheckBoxIcon from '@material-ui/icons/CheckBox'

const EmailMultiSelectField = ({
  t,
  control,
  name,
  label,
  emails,
  setValue,
  defaultValue = [],
  disabled = false,
  className,
  errors,
  clearErrors,
  setIsFormEdit
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const filteredEmails = emails.filter((el) =>
    `${el.firstname} ${el.lastname} ${el.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase().trim())
  )
  const {
    fieldState: { invalid }
  } = useController({ name, control, defaultValue })

  const allEmails = emails.map((e) => e.email)
  const favoriteEmails = emails
    .filter((e) => e.pinned_fav_lightentrepreneur)
    .map((e) => e.email)

  const renderLabel = (selected) => {
    const isAll = selected.length === allEmails.length
    const isFavoriteOnly =
      favoriteEmails.every((fav) => selected.includes(fav)) &&
      selected.length === favoriteEmails.length &&
      favoriteEmails.length > 0
    const isFavoritePlus =
      favoriteEmails.every((fav) => selected.includes(fav)) &&
      selected.length > favoriteEmails.length &&
      favoriteEmails.length > 0

    const firstSelectedEmail = emails.find((el) => el.email === selected[0])

    if (isAll) return t('employer.assignments.render1')
    if (isFavoriteOnly) return t('employer.assignments.render2')
    if (isFavoritePlus) return t('employer.assignments.render3')
    if (selected.length === 1)
      return `${firstSelectedEmail.firstname} ${firstSelectedEmail.lastname}`
    if (selected.length > 1)
      return `${firstSelectedEmail.firstname} ${
        firstSelectedEmail.lastname
      } ${t('employer.assignments.render4')}`

    return t('employer.assignments.render5')
  }

  return (
    <div>
      <FormControl fullWidth error={invalid} style={{ marginTop: '6px' }}>
        <InputLabel
          shrink
          style={{
            marginTop: '-8px',
            fontSize: '20px',
            color: errors?.offerTo ? 'red' : '#333333'
          }}
        >
          {label}
        </InputLabel>

        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const isAllSelected =
              field.value.length === allEmails.length && emails.length > 0
            const isAllFavoritesSelected =
              favoriteEmails.every((fav) => field.value.includes(fav)) &&
              field.value.length === favoriteEmails.length &&
              emails.length > 0
            const isAllFavoritesAndSomeOthersSelected =
              favoriteEmails.every((fav) => field.value.includes(fav)) &&
              field.value.length > favoriteEmails.length &&
              emails.length > 0
            const selectedEmailsNotInFavorites = field.value?.filter(
              (email) => !favoriteEmails.some((e) => e === email)
            )

            const emailsToDisplay =
              searchTerm === '' &&
              isAllFavoritesAndSomeOthersSelected &&
              !isAllSelected
                ? emails.filter((email) =>
                    selectedEmailsNotInFavorites.includes(email.email)
                  )
                : searchTerm !== ''
                ? filteredEmails
                : searchTerm === '' && !isAllSelected && !isAllFavoritesSelected
                ? emails.filter((email) => field.value.includes(email.email))
                : []
            return (
              <Select
                multiple
                value={field.value}
                disableUnderline={true}
                onChange={(e) => {
                  const value = e.target.value.filter(
                    (v) =>
                      v !== 'all' && v !== 'favorites' && v !== 'search_field'
                  )
                  field.onChange(value)
                  clearErrors(['offerTo'])
                  setIsFormEdit(true)
                  setValue('offer_to_industry', [])
                }}
                renderValue={renderLabel}
                disabled={disabled}
                className={className}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  style: {
                    maxHeight: '400px'
                  }
                }}
              >
                <MenuItem disableRipple disableTouchRipple value="search_field">
                  <TextField
                    autoFocus
                    label={t('employer.assignments.label22')}
                    value={searchTerm}
                    onChange={(e) => {
                      e.stopPropagation() // Prevent Select menu interaction
                      setSearchTerm(e.target.value)
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation() // Prevent default select behavior
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: { fontSize: '16px' }
                    }}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#f5f5f5'
                    }}
                  />
                </MenuItem>
                {allEmails.length > 0 && searchTerm === '' ? (
                  <MenuItem value="all" style={{ padding: 5 }}>
                    <Checkbox
                      checked={isAllSelected}
                      checkedIcon={<CheckBoxIcon style={{ fill: '#F67131' }} />}
                      onChange={(e) => {
                        setValue(name, e.target.checked ? allEmails : [])
                        setSearchTerm('')
                      }}
                    />
                    <ListItemText
                      primary={t('employer.assignments.render1')}
                      primaryTypographyProps={{ style: { fontSize: '13px' } }}
                    />
                  </MenuItem>
                ) : null}
                {favoriteEmails.length > 0 && searchTerm === '' ? (
                  <MenuItem value="favorites" style={{ padding: 5 }}>
                    <Checkbox
                      checked={
                        isAllFavoritesSelected ||
                        isAllFavoritesAndSomeOthersSelected
                      }
                      checkedIcon={<CheckBoxIcon style={{ fill: '#F67131' }} />}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const merged = Array.from(
                            new Set([...field.value, ...favoriteEmails])
                          )
                          setValue(name, merged)
                        } else {
                          const filtered = field.value.filter(
                            (email) => !favoriteEmails.includes(email)
                          )
                          setValue(name, filtered)
                        }
                        setSearchTerm('')
                      }}
                    />
                    <ListItemText
                      primary={t('employer.assignments.render2')}
                      primaryTypographyProps={{ style: { fontSize: '13px' } }}
                    />
                  </MenuItem>
                ) : null}
                {emailsToDisplay.map(({ email, firstname, lastname }) => (
                  <MenuItem key={email} value={email} style={{ padding: 5 }}>
                    <Checkbox
                      checked={field.value.includes(email)}
                      checkedIcon={<CheckBoxIcon style={{ fill: '#F67131' }} />}
                    />
                    <ListItemText
                      primary={`${firstname} ${lastname} (${email})`}
                      primaryTypographyProps={{ style: { fontSize: '13px' } }}
                    />
                  </MenuItem>
                ))}
              </Select>
            )
          }}
        />
      </FormControl>
    </div>
  )
}

export default EmailMultiSelectField
